import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container } from '../ui/Container';
import { HeroSecondaryContent } from './HeroSecondary.content';
import { Header } from '../Header/Header';
import { HeroTitleSvg } from './assets/HeroTitleSvg';
import { ApartmentMonthIcon } from './assets/ApartmentMonthIcon';
import { PromoArrowIcon } from './assets/PromoArrowIcon';
import { Button } from '../ui/Button';
import styles from './HeroSecondary.module.sass';

import cloud from './assets/cloud-1.webp';
import cloud2 from './assets/cloud-2.webp';
import ExtraClouds from './assets/ExtraClouds.webp';

gsap.registerPlugin(ScrollTrigger);

const CLOUD_SPEEDS = [80, 140, 40];

export function HeroSecondary({ clouds = false }) {
  const c1 = useRef(null);
  const c2 = useRef(null);
  const c3 = useRef(null);
  const c4 = useRef(null);
  const c5 = useRef(null);
  const isShowExtraClouds = useRef(clouds);

  useEffect(() => {
    const scrollTriggerConfig = (ref) => ({
      trigger: ref.current.closest('section'),
      start: 'top top',
      end: 'bottom top',
      scrub: true,
    });

    const yRefs = [c1, c3, c4];
    const yTriggers = yRefs.map((ref, i) => {
      if (!ref.current) return null;
      return gsap.to(ref.current, {
        y: CLOUD_SPEEDS[i],
        ease: 'none',
        scrollTrigger: scrollTriggerConfig(ref),
      });
    });

    const c2Trigger = c2.current
      ? gsap.to(c2.current, { x: 250, ease: 'none', scrollTrigger: scrollTriggerConfig(c2) })
      : null;

    const c5Trigger = c5.current
      ? gsap.to(c5.current, {
          x: 55,
          ease: 'none',
          scrollTrigger: scrollTriggerConfig(c5),
        })
      : null;

    const triggers = [...yTriggers, c2Trigger, c5Trigger];

    return () => {
      triggers.forEach((t) => t && t.scrollTrigger && t.scrollTrigger.kill());
      triggers.forEach((t) => t && t.kill());
    };
  }, []);

  return (
    <section
      className={
        styles.hero + ' ' + (isShowExtraClouds.current ? styles['hero_with-extra-clouds'] : '')
      }
      id="top"
    >
      <div className={styles['hero__frame']}>
        <Header />

        <Container className={styles['hero__body']}>
          <h1 className={styles['hero__title']} aria-label={HeroSecondaryContent.backgroundWord}>
            <HeroTitleSvg className={styles['hero__title-svg']} />
          </h1>

          <div
            className={styles['hero__caption-left']}
            dangerouslySetInnerHTML={{ __html: HeroSecondaryContent.leftText }}
          />

          <div className={styles['hero__caption-right']}>
            <p
              className={styles['hero__headline']}
              dangerouslySetInnerHTML={{ __html: HeroSecondaryContent.rightTitle }}
            />
            <p
              className={styles['hero__subheadline']}
              dangerouslySetInnerHTML={{ __html: HeroSecondaryContent.rightText }}
            />
          </div>
        </Container>
      </div>
      <div className={styles['hero__main-image']}></div>
      {!isShowExtraClouds.current && (
        <div className={styles['hero__promo']}>
          <div className={styles['hero__promo-icon']}>
            <ApartmentMonthIcon />
          </div>
          <div
            className={styles['hero__promo-text']}
            dangerouslySetInnerHTML={{ __html: HeroSecondaryContent.promo.text }}
          />
          <Button
            className={styles['hero__promo-button']}
            ariaLabel={HeroSecondaryContent.promo.buttonLabel}
            href={HeroSecondaryContent.promo.buttonHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles['hero__promo-button-label']}>
              {HeroSecondaryContent.promo.buttonLabel}
            </span>
            <PromoArrowIcon className={styles['hero__promo-button-icon']} />
          </Button>
        </div>
      )}
      <img ref={c1} src={cloud} alt="Облако" className={styles['hero__cloud-1']} />
      <img ref={c2} src={cloud} alt="Облако" className={styles['hero__cloud-2']} />
      <img ref={c3} src={cloud2} alt="Облако" className={styles['hero__cloud-3']} />
      <img ref={c4} src={cloud} alt="Облако" className={styles['hero__cloud-4']} />
      <span className={styles['hero__cloud-5-wrap']}>
        <img ref={c5} src={cloud2} alt="Облако" className={styles['hero__cloud-5']} />
      </span>
      {isShowExtraClouds.current && (
        <>
          <img src={ExtraClouds} alt="Облако" className={styles['hero__cloud-bottom']} />
        </>
      )}
    </section>
  );
}
